import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "../utils/axiosInstance";
import "bootstrap-icons/font/bootstrap-icons.css";

const API_URL = "/abana_barimwo_mirire_mibi";

function AbanaMirireMibi() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editId, setEditId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterNutrition, setFilterNutrition] = useState("all");
  const [filterAssisted, setFilterAssisted] = useState("all");
  const [filterGender, setFilterGender] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [activeTab, setActiveTab] = useState("data");
  const [sortKey, setSortKey] = useState("amazina");
  const [sortDir, setSortDir] = useState("asc");
  const [expandedCard, setExpandedCard] = useState(null);
  const [errors, setErrors] = useState({});

  const itemsPerPage = 10;

  const initialForm = {
    amazina: "", imyaka: "", igitsina: "Gabo",
    umubyeyi_wa_mwana: "", aderesi: "",
    urwego_rw_imirire: "Bisanzwe", afashijwe: "Oya"
  };

  const [form, setForm] = useState(initialForm);

  const nutritionConfig = {
    Bikabije: { color: "#ef4444", bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.18)", label: "Bikabije", icon: "bi-exclamation-triangle-fill" },
    Bito: { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.18)", label: "Bito", icon: "bi-exclamation-circle" },
    Bisanzwe: { color: "#10b981", bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.18)", label: "Bisanzwe", icon: "bi-check-circle-fill" },
  };

  const showAlertMsg = useCallback((message, variant = "ok") => {
    setAlert({ message, variant });
    setTimeout(() => setAlert(null), 4500);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const raw = res.data;
      setData(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
    } catch {
      showAlertMsg("Habaye ikibazo mu gutwara amakuru", "err");
    } finally {
      setLoading(false);
    }
  }, [showAlertMsg]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => {
    if (!data.length) return {
      total: 0, bikabije: 0, bito: 0, bisanzwe: 0,
      afashijwe: 0, ntibafashijwe: 0, gabo: 0, gore: 0, avgAge: 0
    };
    const totalAge = data.reduce((s, d) => s + Number(d.imyaka || 0), 0);
    return {
      total: data.length,
      bikabije: data.filter(d => d.urwego_rw_imirire === "Bikabije").length,
      bito: data.filter(d => d.urwego_rw_imirire === "Bito").length,
      bisanzwe: data.filter(d => d.urwego_rw_imirire === "Bisanzwe").length,
      afashijwe: data.filter(d => d.afashijwe === "Yego").length,
      ntibafashijwe: data.filter(d => d.afashijwe === "Oya").length,
      gabo: data.filter(d => d.igitsina === "Gabo").length,
      gore: data.filter(d => d.igitsina === "Gore").length,
      avgAge: (totalAge / data.length).toFixed(1),
    };
  }, [data]);

  const filtered = useMemo(() => {
    let result = [...data];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(i =>
        i.amazina?.toLowerCase().includes(q) ||
        i.umubyeyi_wa_mwana?.toLowerCase().includes(q) ||
        i.aderesi?.toLowerCase().includes(q)
      );
    }
    if (filterNutrition !== "all") result = result.filter(i => i.urwego_rw_imirire === filterNutrition);
    if (filterAssisted !== "all") result = result.filter(i => i.afashijwe === filterAssisted);
    if (filterGender !== "all") result = result.filter(i => i.igitsina === filterGender);
    if (sortKey) {
      result.sort((a, b) => {
        let aV = a[sortKey] || "";
        let bV = b[sortKey] || "";
        if (sortKey === "imyaka") { aV = Number(aV); bV = Number(bV); }
        else { aV = aV.toString().toLowerCase(); bV = bV.toString().toLowerCase(); }
        if (aV < bV) return sortDir === "asc" ? -1 : 1;
        if (aV > bV) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [data, searchTerm, filterNutrition, filterAssisted, filterGender, sortKey, sortDir]);

  const urgentCases = useMemo(() =>
    data.filter(d => d.urwego_rw_imirire === "Bikabije" && d.afashijwe === "Oya")
  , [data]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterNutrition, filterAssisted, filterGender]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const firstIdx = (currentPage - 1) * itemsPerPage;

  const handleSort = (key) => {
    setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc");
    setSortKey(key);
  };

  const validateForm = () => {
    const e = {};
    if (!form.amazina?.trim()) e.amazina = "Amazina arakenewe";
    if (!form.imyaka || form.imyaka < 0 || form.imyaka > 18) e.imyaka = "Imyaka 0–18 gusa";
    if (!form.umubyeyi_wa_mwana?.trim()) e.umubyeyi_wa_mwana = "Umubyeyi arakenewe";
    if (!form.aderesi?.trim()) e.aderesi = "Aderesi irakenewe";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (ev) => {
    const { name, value } = ev.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: "" }));
  };

  const resetForm = () => { setForm(initialForm); setEditId(null); setErrors({}); };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, form);
        showAlertMsg("Byahinduwe neza!");
      } else {
        await axios.post(API_URL, form);
        showAlertMsg("Umwana yongerwemo neza!");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      showAlertMsg("Ikibazo cyabaye mu kubika", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({ ...item });
    setEditId(item.id);
    setErrors({});
    setShowModal(true);
  };

  const confirmDelete = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/${deleteTarget.id}`);
      showAlertMsg(`${deleteTarget.amazina} yasibwe neza!`);
      loadData();
    } catch {
      showAlertMsg("Gusiba byananiranye", "err");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const openDetail = (item) => { setDetailItem(item); setShowDetailModal(true); };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return "#7c3aed";
    const colors = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return colors[Math.abs(h) % colors.length];
  };

  const getNutritionTag = (level) => {
    const cfg = nutritionConfig[level];
    if (!cfg) return null;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 10px", borderRadius: 7, fontSize: "0.72rem", fontWeight: 650,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      }}>
        <i className={`bi ${cfg.icon}`} style={{ fontSize: "0.65rem" }}></i>
        {cfg.label}
      </span>
    );
  };

  const hasActiveFilters = searchTerm || filterNutrition !== "all" || filterAssisted !== "all" || filterGender !== "all";

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterNutrition("all");
    setFilterAssisted("all");
    setFilterGender("all");
  };

  const exportCSV = () => {
    const h = ["Amazina", "Imyaka", "Igitsina", "Umubyeyi", "Aderesi", "Imirire", "Afashijwe"];
    const rows = filtered.map(i => [i.amazina, i.imyaka, i.igitsina, i.umubyeyi_wa_mwana, i.aderesi || "-", i.urwego_rw_imirire, i.afashijwe]);
    const csv = [h.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `imirire_mibi_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    showAlertMsg("Excel yakoboye neza!");
  };

  // ── DropFilter ──
  const DropFilter = ({ icon, label, value, options, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const isActive = value !== "all";
    useEffect(() => {
      if (!open) return;
      const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener("mousedown", handler);
      document.addEventListener("touchstart", handler);
      return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("touchstart", handler); };
    }, [open]);
    return (
      <div ref={ref} style={{ position: "relative" }}>
        <button className={`mm-filter-btn${isActive ? " active" : ""}`} onClick={() => setOpen(!open)}>
          <i className={`bi bi-${icon}`}></i>
          <span>{isActive ? options.find(o => o.v === value)?.l || label : label}</span>
          <i className={`bi bi-chevron-${open ? "up" : "down"}`} style={{ marginLeft: "auto", fontSize: "0.6rem", opacity: 0.4 }}></i>
        </button>
        {open && (
          <div className="mm-dropdown">
            {options.map(o => (
              <button key={o.v} className={`mm-drop-item${value === o.v ? " sel" : ""}`}
                onClick={() => { onChange(o.v); setOpen(false); }}>
                {value === o.v && <i className="bi bi-check2" style={{ color: "#ef4444" }}></i>}
                {o.l}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <i className="bi bi-chevron-expand" style={{ fontSize: "0.55rem", opacity: 0.25, marginLeft: 4 }}></i>;
    return sortDir === "asc"
      ? <i className="bi bi-chevron-up" style={{ fontSize: "0.55rem", color: "#ef4444", marginLeft: 4 }}></i>
      : <i className="bi bi-chevron-down" style={{ fontSize: "0.55rem", color: "#ef4444", marginLeft: 4 }}></i>;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxV = 5;
    let s = Math.max(1, currentPage - Math.floor(maxV / 2));
    let e = Math.min(totalPages, s + maxV - 1);
    if (e - s + 1 < maxV) s = Math.max(1, e - maxV + 1);
    if (s > 1) { pages.push(1); if (s > 2) pages.push("..."); }
    for (let i = s; i <= e; i++) pages.push(i);
    if (e < totalPages) { if (e < totalPages - 1) pages.push("..."); pages.push(totalPages); }

    return (
      <div className="mm-pagination">
        <span className="mm-pg-info">
          {firstIdx + 1}–{Math.min(firstIdx + itemsPerPage, filtered.length)} kuri {filtered.length}
        </span>
        <div className="mm-pg-btns">
          <button className="mm-pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
            <i className="bi bi-chevron-double-left"></i>
          </button>
          <button className="mm-pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          {pages.map((p, i) =>
            p === "..." ? <span key={`e${i}`} className="mm-pg-ellipsis">…</span> : (
              <button key={p} className={`mm-pg-btn${p === currentPage ? " active" : ""}`} onClick={() => setCurrentPage(p)}>{p}</button>
            )
          )}
          <button className="mm-pg-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <button className="mm-pg-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
            <i className="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&display=swap');

        .mm-root *, .mm-root *::before, .mm-root *::after {
          box-sizing: border-box; margin: 0; padding: 0;
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          -webkit-tap-highlight-color: transparent;
        }
        .mm-root { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        .mm-root ::-webkit-scrollbar { width: 5px; height: 5px; }
        .mm-root ::-webkit-scrollbar-track { background: transparent; }
        .mm-root ::-webkit-scrollbar-thumb { background: rgba(239,68,68,0.2); border-radius: 10px; }

        @keyframes mmUp { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:translateY(0); } }
        @keyframes mmScale { from { opacity:0; transform:scale(0.94); } to { opacity:1; transform:scale(1); } }
        @keyframes mmSlide { from { opacity:0; transform:translateX(-14px); } to { opacity:1; transform:translateX(0); } }
        @keyframes mmPop { 0% { opacity:0; transform:translateY(-12px) scale(0.94); } 60% { transform:translateY(2px) scale(1.01); } 100% { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes mmGrad { 0%,100% { background-position:0% 50%; } 50% { background-position:100% 50%; } }
        @keyframes mmDot { 0%,80%,100% { transform:scale(0); } 40% { transform:scale(1); } }
        @keyframes mmFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-5px); } }
        @keyframes mmCount { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes mmShake { 0%,100% { transform:translateX(0); } 25% { transform:translateX(-4px); } 75% { transform:translateX(4px); } }
        @keyframes mmPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.7; transform:scale(1.06); } }
        @keyframes mmPulseRing { 0% { box-shadow:0 0 0 0 rgba(239,68,68,0.15); } 100% { box-shadow:0 0 0 12px rgba(239,68,68,0); } }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }

        .mm-shell {
          min-height: 100vh; min-height: 100dvh;
          background: #000; color: #fff;
          position: relative; overflow-x: hidden;
        }
        .mm-shell::before {
          content: ''; position: fixed; inset: 0;
          background:
            radial-gradient(ellipse 65% 45% at 15% 10%, rgba(239,68,68,0.05) 0%, transparent 52%),
            radial-gradient(ellipse 50% 65% at 85% 85%, rgba(245,158,11,0.04) 0%, transparent 48%),
            radial-gradient(ellipse 35% 35% at 50% 50%, rgba(16,185,129,0.025) 0%, transparent 40%);
          pointer-events: none; z-index: 0;
        }
        .mm-layer { position: relative; z-index: 1; }
        .mm-wrap { max-width: 1420px; margin: 0 auto; padding: 20px 16px 44px; }

        /* HEADER */
        .mm-header { text-align: center; margin-bottom: 28px; animation: mmUp 0.5s ease; }
        .mm-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 14px; border-radius: 10px;
          background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.16);
          color: #f87171; font-size: 0.66rem; font-weight: 700;
          letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 12px;
        }
        .mm-title {
          font-size: clamp(1.5rem, 5vw, 2.6rem); font-weight: 900; line-height: 1.08; margin-bottom: 6px;
          background: linear-gradient(135deg, #ef4444, #f59e0b, #ef4444);
          background-size: 300% 300%; animation: mmGrad 7s ease infinite;
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .mm-sub { color: rgba(255,255,255,0.26); font-size: clamp(0.82rem,2vw,0.95rem); max-width: 480px; margin: 0 auto; }

        /* ACTIONS */
        .mm-actions {
          display: flex; flex-wrap: wrap; gap: 8px;
          justify-content: center; margin-bottom: 24px; animation: mmUp 0.55s ease;
        }
        .mm-btn {
          padding: 10px 20px; border-radius: 12px; font-weight: 650; font-size: 0.84rem;
          font-family: inherit; cursor: pointer; transition: all 0.3s ease;
          display: inline-flex; align-items: center; gap: 7px;
          border: none; white-space: nowrap; position: relative; overflow: hidden;
        }
        .mm-btn::after {
          content: ''; position: absolute; top: 50%; left: 50%;
          width: 0; height: 0; background: rgba(255,255,255,0.08);
          border-radius: 50%; transform: translate(-50%,-50%);
          transition: width 0.5s, height 0.5s;
        }
        .mm-btn:active::after { width: 300px; height: 300px; }
        .mm-btn-primary {
          background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff;
          box-shadow: 0 3px 14px rgba(239,68,68,0.22);
        }
        .mm-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(239,68,68,0.38); }
        .mm-btn-primary:active { transform: translateY(0) scale(0.98); }
        .mm-btn-ghost {
          background: transparent; color: rgba(255,255,255,0.48);
          border: 1.5px solid rgba(255,255,255,0.07);
        }
        .mm-btn-ghost:hover { color: #fff; border-color: rgba(255,255,255,0.18); background: rgba(255,255,255,0.025); }
        .mm-btn-green {
          background: rgba(16,185,129,0.1); color: #34d399;
          border: 1.5px solid rgba(16,185,129,0.18);
        }
        .mm-btn-green:hover { background: rgba(16,185,129,0.16); }
        .mm-btn-danger {
          background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff;
          box-shadow: 0 3px 14px rgba(239,68,68,0.22);
        }
        .mm-btn-danger:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(239,68,68,0.38); }
        .mm-btn-amber {
          background: rgba(245,158,11,0.1); color: #fbbf24;
          border: 1.5px solid rgba(245,158,11,0.18);
        }
        .mm-btn-amber:hover { background: rgba(245,158,11,0.16); }

        /* ALERT */
        .mm-alert {
          padding: 12px 16px; border-radius: 14px; display: flex; align-items: center;
          gap: 10px; font-weight: 500; font-size: 0.84rem; margin-bottom: 18px;
          animation: mmPop 0.4s cubic-bezier(0.34,1.56,0.64,1); border: 1px solid;
        }
        .mm-alert-ok { background: rgba(34,197,94,0.06); border-color: rgba(34,197,94,0.15); color: #4ade80; }
        .mm-alert-err { background: rgba(239,68,68,0.06); border-color: rgba(239,68,68,0.15); color: #f87171; }
        .mm-alert-warn { background: rgba(245,158,11,0.06); border-color: rgba(245,158,11,0.15); color: #fbbf24; }
        .mm-alert-icon {
          width: 32px; height: 32px; min-width: 32px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center; font-size: 0.9rem;
        }
        .mm-alert-x {
          margin-left: auto; background: none; border: none; color: inherit;
          cursor: pointer; padding: 3px; opacity: 0.5; font-size: 0.85rem; transition: opacity 0.2s;
        }
        .mm-alert-x:hover { opacity: 1; }

        /* TABS */
        .mm-tabs {
          display: flex; gap: 4px; margin-bottom: 24px;
          background: rgba(10,10,22,0.5); border: 1px solid rgba(255,255,255,0.04);
          border-radius: 14px; padding: 4px; animation: mmUp 0.6s ease;
          overflow-x: auto; -webkit-overflow-scrolling: touch;
        }
        .mm-tab {
          flex: 1; min-width: 0; padding: 12px 16px; border-radius: 11px;
          background: transparent; border: none; color: rgba(255,255,255,0.4);
          font-size: 0.82rem; font-weight: 600; cursor: pointer;
          transition: all 0.3s; font-family: inherit;
          display: flex; align-items: center; justify-content: center; gap: 7px;
          white-space: nowrap;
        }
        .mm-tab:hover { color: rgba(255,255,255,0.7); background: rgba(255,255,255,0.02); }
        .mm-tab.active { background: rgba(239,68,68,0.08); color: #f87171; }
        .mm-tab-badge {
          padding: 1px 7px; border-radius: 6px; font-size: 0.65rem; font-weight: 700;
          background: rgba(239,68,68,0.15); color: #f87171; animation: mmPulse 2.4s infinite;
        }

        /* STATS */
        .mm-stats {
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 10px; margin-bottom: 24px; animation: mmUp 0.65s ease;
        }
        .mm-stat {
          background: linear-gradient(150deg, rgba(12,12,24,0.9), rgba(6,6,14,0.96));
          backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.04); border-radius: 16px;
          padding: 18px 10px; text-align: center;
          transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94);
          position: relative; overflow: hidden; cursor: default;
        }
        .mm-stat::after {
          content: ''; position: absolute; top: 0; left: 0; right: 0;
          height: 2.5px; border-radius: 16px 16px 0 0; opacity: 0; transition: opacity 0.35s;
        }
        .mm-stat:hover { transform: translateY(-4px); border-color: rgba(255,255,255,0.07); }
        .mm-stat:hover::after { opacity: 1; }
        .mm-stat.red::after { background: linear-gradient(90deg, #ef4444, #f87171); }
        .mm-stat.red:hover { box-shadow: 0 12px 36px rgba(239,68,68,0.08); }
        .mm-stat.amber::after { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
        .mm-stat.amber:hover { box-shadow: 0 12px 36px rgba(245,158,11,0.08); }
        .mm-stat.green::after { background: linear-gradient(90deg, #10b981, #34d399); }
        .mm-stat.green:hover { box-shadow: 0 12px 36px rgba(16,185,129,0.1); }
        .mm-stat.blue::after { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
        .mm-stat.blue:hover { box-shadow: 0 12px 36px rgba(59,130,246,0.1); }
        .mm-stat-icon {
          width: 40px; height: 40px; border-radius: 12px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 1.05rem; margin-bottom: 8px; transition: transform 0.35s;
        }
        .mm-stat:hover .mm-stat-icon { transform: scale(1.12) rotate(-4deg); }
        .mm-stat-val {
          font-size: clamp(1.3rem,2.8vw,1.9rem); font-weight: 800; color: #fff;
          line-height: 1; margin-bottom: 3px; letter-spacing: -0.03em; animation: mmCount 0.5s;
        }
        .mm-stat-lbl { font-size: 0.58rem; color: rgba(255,255,255,0.3); font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; }
        .mm-stat-sub { font-size: 0.6rem; color: rgba(255,255,255,0.18); margin-top: 3px; }
        .mm-progress { height: 3px; border-radius: 2px; background: rgba(255,255,255,0.03); margin-top: 8px; overflow: hidden; }
        .mm-progress-bar { height: 100%; border-radius: 2px; transition: width 0.8s ease; }

        /* GLASS */
        .mm-glass {
          background: linear-gradient(145deg, rgba(10,10,22,0.88), rgba(5,5,12,0.96));
          backdrop-filter: blur(20px) saturate(1.2); -webkit-backdrop-filter: blur(20px) saturate(1.2);
          border: 1px solid rgba(255,255,255,0.045); border-radius: 18px;
          transition: border-color 0.4s, box-shadow 0.4s; overflow: hidden;
        }
        .mm-glass:hover { border-color: rgba(239,68,68,0.06); box-shadow: 0 4px 24px rgba(0,0,0,0.18); }
        .mm-glass-body { padding: clamp(16px,3.5vw,36px); }

        /* FILTERS */
        .mm-filters {
          display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
          margin-bottom: 20px; animation: mmUp 0.7s ease;
        }
        .mm-search-wrap { position: relative; flex: 1; min-width: 200px; }
        .mm-search-icon {
          position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
          color: rgba(255,255,255,0.16); font-size: 0.9rem;
          transition: color 0.3s; z-index: 2; pointer-events: none;
        }
        .mm-search-wrap:focus-within .mm-search-icon { color: #ef4444; }
        .mm-search {
          width: 100%; padding: 11px 15px 11px 40px;
          background: rgba(10,10,24,0.5); border: 1.5px solid rgba(255,255,255,0.05);
          border-radius: 12px; color: #fff; font-size: 0.86rem; font-weight: 500;
          outline: none; transition: all 0.3s; font-family: inherit;
        }
        .mm-search::placeholder { color: rgba(255,255,255,0.18); }
        .mm-search:focus { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.08); background: rgba(14,14,32,0.75); }
        .mm-filter-btn {
          padding: 11px 14px; background: rgba(10,10,24,0.5);
          border: 1.5px solid rgba(255,255,255,0.045); border-radius: 12px;
          color: rgba(255,255,255,0.55); font-size: 0.8rem; font-weight: 600;
          cursor: pointer; transition: all 0.3s; display: flex; align-items: center;
          gap: 6px; white-space: nowrap; font-family: inherit;
        }
        .mm-filter-btn:hover { border-color: rgba(255,255,255,0.1); color: rgba(255,255,255,0.8); }
        .mm-filter-btn.active { border-color: rgba(239,68,68,0.25); color: #f87171; background: rgba(239,68,68,0.05); }
        .mm-dropdown {
          position: absolute; top: calc(100% + 5px); left: 0; right: 0;
          background: rgba(12,12,26,0.97); backdrop-filter: blur(14px);
          border: 1px solid rgba(255,255,255,0.07); border-radius: 12px;
          box-shadow: 0 10px 36px rgba(0,0,0,0.5); z-index: 50;
          overflow: hidden; animation: mmScale 0.18s ease;
        }
        .mm-drop-item {
          padding: 9px 13px; font-size: 0.8rem; color: rgba(255,255,255,0.55);
          cursor: pointer; transition: all 0.2s; display: flex; align-items: center;
          gap: 7px; border: none; background: none; width: 100%; text-align: left; font-family: inherit;
        }
        .mm-drop-item:hover { background: rgba(239,68,68,0.06); color: #fff; }
        .mm-drop-item.sel { color: #f87171; background: rgba(239,68,68,0.04); }

        /* VIEW TOGGLE */
        .mm-view-toggle { display: flex; border-radius: 10px; border: 1px solid rgba(255,255,255,0.06); overflow: hidden; }
        .mm-view-btn {
          background: transparent; border: none; color: rgba(255,255,255,0.3);
          padding: 8px 12px; cursor: pointer; transition: all 0.25s;
          display: flex; align-items: center; justify-content: center; font-size: 0.88rem;
        }
        .mm-view-btn:hover { color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.02); }
        .mm-view-btn.active { background: rgba(239,68,68,0.08); color: #f87171; }

        /* FILTER TAGS */
        .mm-active-filters {
          display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
          margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid rgba(255,255,255,0.03);
        }
        .mm-filter-tag {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 8px; font-size: 0.72rem; font-weight: 600;
          cursor: pointer; transition: all 0.2s; border: none; font-family: inherit;
        }
        .mm-filter-tag-item { background: rgba(239,68,68,0.08); color: #f87171; border: 1px solid rgba(239,68,68,0.15); }
        .mm-filter-tag-item:hover { background: rgba(239,68,68,0.15); }
        .mm-filter-tag-clear { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.4); border: 1px solid rgba(255,255,255,0.06); }
        .mm-filter-tag-clear:hover { background: rgba(255,255,255,0.08); }

        /* TABLE */
        .mm-table-wrap { border-radius: 13px; overflow: hidden; border: 1px solid rgba(255,255,255,0.03); }
        .mm-table-scroll { max-height: 520px; overflow-y: auto; }
        .mm-tbl { width: 100%; border-collapse: separate; border-spacing: 0; }
        .mm-tbl thead { position: sticky; top: 0; z-index: 5; }
        .mm-tbl th {
          padding: 12px 15px; font-size: 0.62rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.1em;
          color: rgba(255,255,255,0.28); border-bottom: 1px solid rgba(255,255,255,0.04);
          background: rgba(6,6,14,0.96); white-space: nowrap; text-align: left;
          cursor: pointer; user-select: none; transition: color 0.2s;
        }
        .mm-tbl th:hover { color: #f87171; }
        .mm-tbl td {
          padding: 12px 15px; font-size: 0.82rem; color: rgba(255,255,255,0.65);
          border-bottom: 1px solid rgba(255,255,255,0.02); vertical-align: middle;
        }
        .mm-tbl tbody tr { transition: all 0.2s; }
        .mm-tbl tbody tr:hover { background: rgba(239,68,68,0.025); }
        .mm-tbl tbody tr.urgent { background: rgba(239,68,68,0.04); }
        .mm-tbl tbody tr.urgent:hover { background: rgba(239,68,68,0.07); }
        .mm-tbl tbody tr:last-child td { border-bottom: none; }

        /* AVATAR */
        .mm-avatar {
          width: 34px; height: 34px; border-radius: 10px;
          display: inline-flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 0.7rem; color: #000; flex-shrink: 0;
          margin-right: 10px; vertical-align: middle; transition: transform 0.3s;
        }
        .mm-tbl tbody tr:hover .mm-avatar { transform: scale(1.08); }

        /* TAGS */
        .mm-tag {
          display: inline-flex; align-items: center; padding: 3px 9px;
          border-radius: 7px; font-size: 0.68rem; font-weight: 650;
          border: 1px solid transparent; white-space: nowrap;
        }
        .mm-tag-blue { background: rgba(59,130,246,0.1); color: #60a5fa; border-color: rgba(59,130,246,0.18); }
        .mm-tag-rose { background: rgba(244,63,94,0.08); color: #fb7185; border-color: rgba(244,63,94,0.15); }
        .mm-tag-green { background: rgba(34,197,94,0.1); color: #4ade80; border-color: rgba(34,197,94,0.18); }
        .mm-tag-red { background: rgba(239,68,68,0.08); color: #f87171; border-color: rgba(239,68,68,0.15); }

        /* ACTION BTNS */
        .mm-act-grp { display: flex; gap: 5px; justify-content: center; }
        .mm-act {
          width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid;
          background: transparent; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.25s; font-size: 0.78rem;
        }
        .mm-act:hover { transform: translateY(-2px); }
        .mm-act:active { transform: translateY(0) scale(0.95); }
        .mm-act.view { border-color: rgba(6,182,212,0.22); color: #22d3ee; }
        .mm-act.view:hover { background: rgba(6,182,212,0.08); box-shadow: 0 3px 10px rgba(6,182,212,0.16); }
        .mm-act.edit { border-color: rgba(245,158,11,0.22); color: #fbbf24; }
        .mm-act.edit:hover { background: rgba(245,158,11,0.08); box-shadow: 0 3px 10px rgba(245,158,11,0.16); }
        .mm-act.del { border-color: rgba(239,68,68,0.22); color: #f87171; }
        .mm-act.del:hover { background: rgba(239,68,68,0.08); box-shadow: 0 3px 10px rgba(239,68,68,0.16); }

        /* GRID */
        .mm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
        .mm-grid-card {
          background: rgba(10,10,22,0.5); border: 1px solid rgba(255,255,255,0.035);
          border-radius: 16px; padding: 18px; transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94);
          position: relative; overflow: hidden;
        }
        .mm-grid-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          opacity: 0; transition: opacity 0.3s;
        }
        .mm-grid-card:hover { border-color: rgba(239,68,68,0.12); transform: translateY(-4px); box-shadow: 0 12px 36px rgba(0,0,0,0.25); }
        .mm-grid-card:hover::before { opacity: 1; }
        .mm-grid-card.urgent::before { background: linear-gradient(90deg, #ef4444, #f59e0b); opacity: 1; }
        .mm-grid-card:not(.urgent)::before { background: linear-gradient(90deg, #10b981, #06b6d4); }
        .mm-grid-card-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
        .mm-grid-avatar {
          width: 48px; height: 48px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 0.95rem; color: #000; flex-shrink: 0; transition: transform 0.3s;
        }
        .mm-grid-card:hover .mm-grid-avatar { transform: scale(1.08) rotate(-3deg); }
        .mm-grid-card-name { font-weight: 700; color: #fff; font-size: 0.95rem; margin-bottom: 2px; }
        .mm-grid-card-sub { font-size: 0.75rem; color: rgba(255,255,255,0.3); }
        .mm-grid-card-tags { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: 12px; }
        .mm-grid-card-row {
          display: flex; justify-content: space-between; padding: 6px 0;
          border-bottom: 1px solid rgba(255,255,255,0.02); font-size: 0.8rem;
        }
        .mm-grid-card-row:last-child { border-bottom: none; }
        .mm-grid-card-lbl { color: rgba(255,255,255,0.28); font-weight: 500; }
        .mm-grid-card-val { color: rgba(255,255,255,0.75); font-weight: 600; text-align: right; }
        .mm-grid-card-actions {
          display: flex; gap: 6px; justify-content: flex-end;
          padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.025);
        }

        /* MOBILE CARDS */
        .mm-mobile-cards { display: none; }
        .mm-mcard {
          background: rgba(10,10,22,0.5); border: 1px solid rgba(255,255,255,0.035);
          border-radius: 14px; padding: 14px; margin-bottom: 8px;
          transition: all 0.3s; animation: mmUp 0.3s ease both;
        }
        .mm-mcard.urgent { border-left: 3px solid #ef4444; }
        .mm-mcard-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
        .mm-mcard-left { display: flex; align-items: center; gap: 10px; }
        .mm-mcard-avatar {
          width: 40px; height: 40px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 0.8rem; color: #000; flex-shrink: 0;
        }
        .mm-mcard-name { font-weight: 700; color: #fff; font-size: 0.92rem; margin-bottom: 3px; }
        .mm-mcard-tags { display: flex; gap: 5px; flex-wrap: wrap; }
        .mm-mcard-row {
          display: flex; justify-content: space-between; padding: 5px 0;
          border-bottom: 1px solid rgba(255,255,255,0.02); font-size: 0.78rem;
        }
        .mm-mcard-row:last-child { border-bottom: none; }
        .mm-mcard-lbl { color: rgba(255,255,255,0.28); font-weight: 500; }
        .mm-mcard-val { color: rgba(255,255,255,0.75); font-weight: 600; text-align: right; }
        .mm-mcard-actions {
          display: flex; gap: 6px; justify-content: flex-end;
          margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.025);
        }
        .mm-mcard-expand { text-align: center; padding: 6px; color: rgba(255,255,255,0.1); font-size: 0.7rem; cursor: pointer; }

        /* PAGINATION */
        .mm-pagination {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; margin-top: 16px; padding-top: 14px;
          border-top: 1px solid rgba(255,255,255,0.035);
        }
        .mm-pg-info { font-size: 0.74rem; color: rgba(255,255,255,0.25); }
        .mm-pg-btns { display: flex; gap: 3px; align-items: center; }
        .mm-pg-btn {
          width: 32px; height: 32px; border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.05); background: transparent;
          color: rgba(255,255,255,0.4); font-size: 0.75rem; font-weight: 600;
          cursor: pointer; transition: all 0.2s; display: flex; align-items: center;
          justify-content: center; font-family: inherit;
        }
        .mm-pg-btn:hover:not(:disabled) { border-color: rgba(239,68,68,0.25); color: #f87171; background: rgba(239,68,68,0.05); }
        .mm-pg-btn.active {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          border-color: transparent; color: #fff; box-shadow: 0 2px 8px rgba(239,68,68,0.25);
        }
        .mm-pg-btn:disabled { opacity: 0.25; cursor: not-allowed; }
        .mm-pg-ellipsis { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.2); }

        /* MODAL */
        .mm-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.72);
          backdrop-filter: blur(6px); z-index: 1000;
          display: flex; align-items: center; justify-content: center;
          padding: 14px; animation: mmUp 0.2s ease;
        }
        .mm-modal {
          background: linear-gradient(155deg, rgba(12,12,26,0.98), rgba(4,4,12,0.99));
          border: 1px solid rgba(255,255,255,0.06); border-radius: 20px;
          width: 100%; max-width: 660px; max-height: 88vh; overflow-y: auto;
          animation: mmScale 0.3s ease;
          box-shadow: 0 20px 70px rgba(0,0,0,0.55), 0 0 80px rgba(239,68,68,0.04);
        }
        .mm-modal-sm { max-width: 420px; }
        .mm-modal-head {
          padding: 18px 22px; border-bottom: 1px solid rgba(255,255,255,0.04);
          display: flex; align-items: center; justify-content: space-between;
          position: sticky; top: 0; background: rgba(12,12,26,0.98);
          backdrop-filter: blur(10px); z-index: 5; border-radius: 20px 20px 0 0;
        }
        .mm-modal-title {
          font-size: 1.1rem; font-weight: 800; color: #fff;
          display: flex; align-items: center; gap: 9px;
        }
        .mm-modal-x {
          width: 34px; height: 34px; border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.07); background: rgba(255,255,255,0.02);
          color: rgba(255,255,255,0.35); cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s; font-size: 0.95rem;
        }
        .mm-modal-x:hover { color: #fff; border-color: rgba(255,255,255,0.18); }
        .mm-modal-body { padding: 22px; }
        .mm-modal-foot {
          padding: 14px 22px; border-top: 1px solid rgba(255,255,255,0.04);
          display: flex; gap: 8px; justify-content: flex-end;
        }

        /* FORM */
        .mm-form-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, 1fr); }
        .mm-form-full { grid-column: 1 / -1; }
        .mm-field { position: relative; }
        .mm-label {
          display: block; font-size: 0.68rem; font-weight: 650;
          color: rgba(255,255,255,0.38); margin-bottom: 6px;
          text-transform: uppercase; letter-spacing: 0.06em; transition: color 0.3s;
        }
        .mm-field:focus-within .mm-label { color: #f87171; }
        .mm-req { color: #f87171; margin-left: 2px; }
        .mm-inp-wrap { position: relative; }
        .mm-inp-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: rgba(255,255,255,0.15); font-size: 0.88rem;
          transition: color 0.3s; z-index: 2; pointer-events: none;
        }
        .mm-field:focus-within .mm-inp-icon { color: #ef4444; }
        .mm-inp {
          width: 100%; padding: 11px 12px 11px 38px;
          background: rgba(10,10,24,0.5); border: 1.5px solid rgba(255,255,255,0.055);
          border-radius: 11px; color: #fff; font-size: 0.88rem; font-weight: 500;
          outline: none; transition: all 0.3s; font-family: inherit;
          -webkit-appearance: none; appearance: none;
        }
        .mm-inp::placeholder { color: rgba(255,255,255,0.14); }
        .mm-inp:focus { border-color: #ef4444; background: rgba(14,14,32,0.75); box-shadow: 0 0 0 3px rgba(239,68,68,0.08); }
        .mm-inp.err { border-color: #ef4444; }
        .mm-sel {
          width: 100%; padding: 11px 34px 11px 38px;
          background: rgba(10,10,24,0.5); border: 1.5px solid rgba(255,255,255,0.055);
          border-radius: 11px; color: #fff; font-size: 0.88rem; font-weight: 500;
          outline: none; transition: all 0.3s; cursor: pointer; font-family: inherit;
          -webkit-appearance: none; appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='rgba(255,255,255,0.28)' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 12px center;
        }
        .mm-sel:focus { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.08); }
        .mm-sel option { background: #0a0a18; color: #fff; }
        .mm-err-msg { font-size: 0.68rem; color: #f87171; margin-top: 3px; font-weight: 500; }

        /* DETAIL */
        .mm-detail-avatar {
          width: 72px; height: 72px; border-radius: 20px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 1.6rem; font-weight: 800; color: #000; margin-bottom: 12px;
        }
        .mm-detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
        .mm-detail-item {
          padding: 14px; background: rgba(10,10,22,0.45);
          border: 1px solid rgba(255,255,255,0.03); border-radius: 12px; transition: border-color 0.3s;
        }
        .mm-detail-item:hover { border-color: rgba(239,68,68,0.1); }
        .mm-detail-lbl {
          font-size: 0.64rem; font-weight: 700; color: rgba(255,255,255,0.26);
          text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px;
          display: flex; align-items: center; gap: 4px;
        }
        .mm-detail-val { font-size: 0.9rem; font-weight: 600; color: #fff; }

        /* DELETE */
        .mm-delete-icon {
          width: 64px; height: 64px; border-radius: 18px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 1.6rem; margin-bottom: 14px;
          background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.15);
          color: #f87171; animation: mmShake 0.5s ease;
        }

        /* URGENT LIST */
        .mm-urgent-item {
          display: flex; align-items: center; gap: 12px;
          padding: 14px; background: rgba(239,68,68,0.03);
          border: 1px solid rgba(239,68,68,0.08); border-radius: 12px;
          margin-bottom: 8px; transition: all 0.3s; cursor: pointer;
        }
        .mm-urgent-item:hover { background: rgba(239,68,68,0.06); border-color: rgba(239,68,68,0.15); transform: translateX(4px); }
        .mm-urgent-avatar {
          width: 42px; height: 42px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 0.8rem; color: #000; flex-shrink: 0;
        }
        .mm-urgent-pulse { width: 8px; height: 8px; border-radius: 50%; background: #ef4444; animation: mmPulse 1.5s infinite; flex-shrink: 0; }

        /* STATISTICS TAB */
        .mm-stats-detail {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px;
        }
        .mm-bar-chart { margin-top: 16px; }
        .mm-bar-row {
          display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
        }
        .mm-bar-label { width: 100px; font-size: 0.78rem; color: rgba(255,255,255,0.5); font-weight: 600; text-align: right; }
        .mm-bar-track {
          flex: 1; height: 8px; border-radius: 4px; background: rgba(255,255,255,0.03); overflow: hidden;
        }
        .mm-bar-fill { height: 100%; border-radius: 4px; transition: width 1s ease; }
        .mm-bar-val { width: 40px; font-size: 0.78rem; font-weight: 700; color: rgba(255,255,255,0.7); }

        /* EMPTY / LOADER */
        .mm-empty { text-align: center; padding: 44px 16px; }
        .mm-empty-icon {
          width: 64px; height: 64px; border-radius: 18px;
          background: rgba(239,68,68,0.05); border: 1px solid rgba(239,68,68,0.08);
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 1.5rem; color: rgba(239,68,68,0.3); margin-bottom: 14px;
          animation: mmFloat 3s ease-in-out infinite;
        }
        .mm-loader { display: flex; gap: 5px; justify-content: center; padding: 44px; }
        .mm-loader span {
          width: 9px; height: 9px; border-radius: 50%; background: #ef4444;
          animation: mmDot 1.4s infinite ease-in-out both;
        }
        .mm-loader span:nth-child(1) { animation-delay: -0.32s; }
        .mm-loader span:nth-child(2) { animation-delay: -0.16s; }

        /* FAB */
        .mm-fab {
          position: fixed; bottom: 24px; right: 24px;
          width: 56px; height: 56px; border-radius: 16px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          border: none; color: #fff; font-size: 1.4rem;
          display: none; align-items: center; justify-content: center;
          box-shadow: 0 8px 30px rgba(239,68,68,0.4);
          z-index: 100; transition: all 0.3s; animation: mmPulseRing 2s infinite;
        }
        .mm-fab:hover { transform: scale(1.1); }
        .mm-fab:active { transform: scale(0.95); }

        .mm-footer {
          text-align: center; margin-top: 32px; padding-bottom: 14px;
          color: rgba(255,255,255,0.08); font-size: 0.7rem; font-weight: 500;
        }

        .mm-spin { animation: spin 1s linear infinite; }

        /* RESPONSIVE */
        @media (max-width: 1024px) {
          .mm-stats { grid-template-columns: repeat(2, 1fr); }
          .mm-stats-detail { grid-template-columns: repeat(2, 1fr); }
          .mm-grid { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
        }
        @media (max-width: 767px) {
          .mm-wrap { padding: 12px 10px 28px; }
          .mm-desktop-tbl { display: none !important; }
          .mm-desktop-grid { display: none !important; }
          .mm-mobile-cards { display: block !important; }
          .mm-fab { display: flex; }
          .mm-form-grid { grid-template-columns: 1fr; }
          .mm-detail-grid { grid-template-columns: 1fr; }
          .mm-stats-detail { grid-template-columns: 1fr; }
          .mm-modal { border-radius: 16px; max-height: 85vh; }
          .mm-modal-body { padding: 16px; }
          .mm-actions { justify-content: stretch; }
          .mm-actions .mm-btn { flex: 1; justify-content: center; }
          .mm-modal-foot { flex-direction: column; }
          .mm-modal-foot .mm-btn { width: 100%; justify-content: center; }
          .mm-pagination { flex-direction: column; align-items: center; }
          .mm-view-toggle { display: none; }
        }
        @media (max-width: 576px) {
          .mm-stat { padding: 14px 8px; border-radius: 12px; }
          .mm-stat-icon { width: 34px; height: 34px; font-size: 0.9rem; border-radius: 10px; margin-bottom: 6px; }
          .mm-stat-val { font-size: 1.15rem; }
          .mm-stat-lbl { font-size: 0.52rem; }
          .mm-glass { border-radius: 14px; }
          .mm-glass-body { padding: 12px; }
          .mm-inp, .mm-sel, .mm-search { font-size: 0.84rem; border-radius: 10px; }
          .mm-btn { padding: 9px 14px; font-size: 0.78rem; border-radius: 10px; }
        }
        @supports (-webkit-touch-callout: none) {
          .mm-shell { min-height: 100dvh; }
          .mm-inp, .mm-sel, .mm-search { font-size: 16px; }
        }
        @media (hover: none) and (pointer: coarse) {
          .mm-stat:hover { transform: none; box-shadow: none; }
          .mm-act:hover { transform: none; }
          .mm-grid-card:hover { transform: none; }
          .mm-urgent-item:hover { transform: none; }
        }
      `}</style>

      <div className="mm-root">
        <div className="mm-shell">
          <div className="mm-layer">
            <div className="mm-wrap">

              {/* HEADER */}
              <div className="mm-header">
                <div className="mm-chip">
                  <i className="bi bi-heart-pulse-fill"></i> Imirire Mibi
                  {urgentCases.length > 0 && <span className="mm-tab-badge">{urgentCases.length} urgent</span>}
                </div>
                <h1 className="mm-title">Abana Bafite Imirire Mibi</h1>
                <p className="mm-sub">Gukurikirana no gufasha abana bafite ibibazo by'imirire mu mudugudu</p>
              </div>

              {/* ACTIONS */}
              <div className="mm-actions">
                <button className="mm-btn mm-btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                  <i className="bi bi-person-plus-fill"></i> Ongeraho Umwana
                </button>
                <button className="mm-btn mm-btn-ghost" onClick={loadData} disabled={loading}>
                  <i className={`bi bi-arrow-clockwise ${loading ? 'mm-spin' : ''}`}></i> Ongera
                </button>
                <button className="mm-btn mm-btn-green" onClick={exportCSV} disabled={!filtered.length}>
                  <i className="bi bi-download"></i> Export Excel
                </button>
              </div>

              {/* ALERT */}
              {alert && (
                <div className={`mm-alert mm-alert-${alert.variant}`}>
                  <div className="mm-alert-icon" style={{
                    background: alert.variant === "ok" ? "rgba(34,197,94,0.1)" :
                      alert.variant === "err" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)"
                  }}>
                    <i className={`bi bi-${alert.variant === "ok" ? "check-lg" : alert.variant === "err" ? "x-lg" : "exclamation-lg"}`}></i>
                  </div>
                  {alert.message}
                  <button className="mm-alert-x" onClick={() => setAlert(null)}>✕</button>
                </div>
              )}

              {/* TABS */}
              <div className="mm-tabs">
                {[
                  { key: "data", icon: "bi-list-ul", label: "Amakuru" },
                  { key: "statistics", icon: "bi-graph-up", label: "Imibare" },
                  { key: "priority", icon: "bi-exclamation-triangle-fill", label: "Byihutirwa", badge: urgentCases.length },
                ].map(tab => (
                  <button key={tab.key}
                    className={`mm-tab ${activeTab === tab.key ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}>
                    <i className={`bi ${tab.icon}`}></i>
                    <span>{tab.label}</span>
                    {tab.badge > 0 && <span className="mm-tab-badge">{tab.badge}</span>}
                  </button>
                ))}
              </div>

              {/* ═══ DATA TAB ═══ */}
              {activeTab === "data" && (
                <>
                  {/* STATS */}
                  <div className="mm-stats">
                    {[
                      { label: "Abana Bose", value: stats.total, icon: "bi-people-fill", color: "#3b82f6", bg: "rgba(59,130,246,0.1)", cls: "blue" },
                      { label: "Bikabije", value: stats.bikabije, icon: "bi-exclamation-triangle-fill", color: "#ef4444", bg: "rgba(239,68,68,0.08)", cls: "red", pulse: true },
                      { label: "Bafashijwe", value: stats.afashijwe, icon: "bi-hand-thumbs-up-fill", color: "#10b981", bg: "rgba(16,185,129,0.1)", cls: "green", pct: stats.total ? Math.round(stats.afashijwe / stats.total * 100) : 0 },
                      { label: "Imyaka y'Imikurire", value: stats.avgAge, icon: "bi-calendar-heart", color: "#f59e0b", bg: "rgba(245,158,11,0.1)", cls: "amber" },
                    ].map((s, i) => (
                      <div key={i} className={`mm-stat ${s.cls}`}>
                        <div className="mm-stat-icon" style={{ background: s.bg, color: s.color }}>
                          <i className={`bi ${s.icon} ${s.pulse ? 'mm-pulse-icon' : ''}`}></i>
                        </div>
                        <div className="mm-stat-val">{s.value}</div>
                        <div className="mm-stat-lbl">{s.label}</div>
                        {s.pct !== undefined && (
                          <div className="mm-progress">
                            <div className="mm-progress-bar" style={{ width: `${s.pct}%`, background: `linear-gradient(90deg,${s.color},${s.color}88)` }}></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* FILTERS */}
                  <div className="mm-filters">
                    <div className="mm-search-wrap">
                      <i className="bi bi-search mm-search-icon"></i>
                      <input className="mm-search" placeholder="Shakisha amazina, umubyeyi, aderesi..."
                        value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                    <DropFilter icon="heart-pulse" label="Imirire" value={filterNutrition} onChange={setFilterNutrition}
                      options={[{ v: "all", l: "Byose" }, { v: "Bikabije", l: "Bikabije" }, { v: "Bito", l: "Bito" }, { v: "Bisanzwe", l: "Bisanzwe" }]} />
                    <DropFilter icon="hand-thumbs-up" label="Ubufasha" value={filterAssisted} onChange={setFilterAssisted}
                      options={[{ v: "all", l: "Byose" }, { v: "Yego", l: "Yafashijwe" }, { v: "Oya", l: "Ntiyafashijwe" }]} />
                    <DropFilter icon="gender-ambiguous" label="Igitsina" value={filterGender} onChange={setFilterGender}
                      options={[{ v: "all", l: "Byose" }, { v: "Gabo", l: "Gabo" }, { v: "Gore", l: "Gore" }]} />
                    <div className="mm-view-toggle">
                      <button className={`mm-view-btn ${viewMode === "table" ? "active" : ""}`} onClick={() => setViewMode("table")}>
                        <i className="bi bi-list-ul"></i>
                      </button>
                      <button className={`mm-view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")}>
                        <i className="bi bi-grid-3x3-gap"></i>
                      </button>
                    </div>
                  </div>

                  {/* DATA PANEL */}
                  <div className="mm-glass" style={{ animation: "mmScale 0.35s ease" }}>
                    <div className="mm-glass-body">

                      {/* Active filters */}
                      {hasActiveFilters && (
                        <div className="mm-active-filters">
                          <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.72rem", fontWeight: 600, marginRight: 4 }}>
                            <i className="bi bi-funnel" style={{ marginRight: 4 }}></i>Filters:
                          </span>
                          {searchTerm && <button className="mm-filter-tag mm-filter-tag-item" onClick={() => setSearchTerm("")}>"{searchTerm}" <i className="bi bi-x"></i></button>}
                          {filterNutrition !== "all" && <button className="mm-filter-tag mm-filter-tag-item" onClick={() => setFilterNutrition("all")}>{filterNutrition} <i className="bi bi-x"></i></button>}
                          {filterAssisted !== "all" && <button className="mm-filter-tag mm-filter-tag-item" onClick={() => setFilterAssisted("all")}>{filterAssisted === "Yego" ? "Yafashijwe" : "Ntiyafashijwe"} <i className="bi bi-x"></i></button>}
                          {filterGender !== "all" && <button className="mm-filter-tag mm-filter-tag-item" onClick={() => setFilterGender("all")}>{filterGender} <i className="bi bi-x"></i></button>}
                          <button className="mm-filter-tag mm-filter-tag-clear" onClick={clearAllFilters}>Siba Byose <i className="bi bi-x-lg" style={{ fontSize: "0.6rem" }}></i></button>
                        </div>
                      )}

                      {loading ? (
                        <div className="mm-loader"><span></span><span></span><span></span></div>
                      ) : currentItems.length === 0 ? (
                        <div className="mm-empty">
                          <div className="mm-empty-icon"><i className="bi bi-heart-pulse"></i></div>
                          <h5 style={{ color: "rgba(255,255,255,0.38)", fontWeight: 700, marginBottom: 4 }}>Nta bana bahari</h5>
                          <p style={{ color: "rgba(255,255,255,0.18)", fontSize: "0.82rem" }}>
                            {hasActiveFilters ? "Hindura ibisuzuma" : "Tangira wongeraho amakuru"}
                          </p>
                          {hasActiveFilters ? (
                            <button className="mm-btn mm-btn-ghost" style={{ marginTop: 10 }} onClick={clearAllFilters}>
                              <i className="bi bi-arrow-counterclockwise"></i> Siba Filters
                            </button>
                          ) : (
                            <button className="mm-btn mm-btn-primary" style={{ marginTop: 10 }} onClick={() => { resetForm(); setShowModal(true); }}>
                              <i className="bi bi-plus-lg"></i> Ongeraho Umwana
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* TABLE */}
                          {viewMode === "table" && (
                            <div className="mm-desktop-tbl">
                              <div className="mm-table-wrap">
                                <div className="mm-table-scroll">
                                  <table className="mm-tbl">
                                    <thead>
                                      <tr>
                                        <th style={{ paddingLeft: 18, width: 50 }}>#</th>
                                        <th onClick={() => handleSort("amazina")}><span style={{ display: "flex", alignItems: "center" }}>Amazina <SortIcon column="amazina" /></span></th>
                                        <th onClick={() => handleSort("imyaka")} style={{ textAlign: "center" }}><span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Imyaka <SortIcon column="imyaka" /></span></th>
                                        <th style={{ textAlign: "center" }}>Igitsina</th>
                                        <th>Umubyeyi</th>
                                        <th>Aderesi</th>
                                        <th style={{ textAlign: "center" }}>Imirire</th>
                                        <th style={{ textAlign: "center" }}>Ubufasha</th>
                                        <th style={{ textAlign: "center", width: 130 }}>Ibikorwa</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {currentItems.map((item, idx) => {
                                        const isUrgent = item.urwego_rw_imirire === "Bikabije" && item.afashijwe === "Oya";
                                        return (
                                          <tr key={item.id} className={isUrgent ? "urgent" : ""} style={{ animation: `mmSlide 0.3s ease ${idx * 0.035}s both` }}>
                                            <td style={{ paddingLeft: 18, color: "rgba(255,255,255,0.18)", fontWeight: 600 }}>{firstIdx + idx + 1}</td>
                                            <td>
                                              <div style={{ display: "flex", alignItems: "center" }}>
                                                <span className="mm-avatar" style={{ background: getAvatarColor(item.amazina) }}>{getInitials(item.amazina)}</span>
                                                <span style={{ fontWeight: 700, color: "#fff" }}>{item.amazina}</span>
                                                {isUrgent && <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444", marginLeft: 6, fontSize: "0.7rem" }}></i>}
                                              </div>
                                            </td>
                                            <td style={{ textAlign: "center" }}>{item.imyaka}</td>
                                            <td style={{ textAlign: "center" }}>
                                              <span className={`mm-tag ${item.igitsina === "Gabo" ? "mm-tag-blue" : "mm-tag-rose"}`}>
                                                <i className={`bi ${item.igitsina === "Gabo" ? "bi-gender-male" : "bi-gender-female"}`} style={{ marginRight: 3, fontSize: "0.6rem" }}></i>
                                                {item.igitsina}
                                              </span>
                                            </td>
                                            <td>{item.umubyeyi_wa_mwana}</td>
                                            <td style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.aderesi || "—"}</td>
                                            <td style={{ textAlign: "center" }}>{getNutritionTag(item.urwego_rw_imirire)}</td>
                                            <td style={{ textAlign: "center" }}>
                                              <span className={`mm-tag ${item.afashijwe === "Yego" ? "mm-tag-green" : "mm-tag-red"}`}>
                                                <i className={`bi ${item.afashijwe === "Yego" ? "bi-check-circle" : "bi-x-circle"}`} style={{ marginRight: 3, fontSize: "0.6rem" }}></i>
                                                {item.afashijwe}
                                              </span>
                                            </td>
                                            <td>
                                              <div className="mm-act-grp">
                                                <button className="mm-act view" title="Reba" onClick={() => openDetail(item)}><i className="bi bi-eye"></i></button>
                                                <button className="mm-act edit" title="Hindura" onClick={() => handleEdit(item)}><i className="bi bi-pencil"></i></button>
                                                <button className="mm-act del" title="Siba" onClick={() => confirmDelete(item)}><i className="bi bi-trash3"></i></button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* GRID */}
                          {viewMode === "grid" && (
                            <div className="mm-desktop-grid">
                              <div className="mm-grid">
                                {currentItems.map((item, idx) => {
                                  const isUrgent = item.urwego_rw_imirire === "Bikabije" && item.afashijwe === "Oya";
                                  return (
                                    <div key={item.id} className={`mm-grid-card ${isUrgent ? "urgent" : ""}`} style={{ animation: `mmUp 0.4s ease ${idx * 0.05}s both` }}>
                                      <div className="mm-grid-card-head">
                                        <div className="mm-grid-avatar" style={{ background: getAvatarColor(item.amazina) }}>{getInitials(item.amazina)}</div>
                                        <div style={{ flex: 1 }}>
                                          <div className="mm-grid-card-name">{item.amazina} {isUrgent && <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444", fontSize: "0.7rem" }}></i>}</div>
                                          <div className="mm-grid-card-sub">{item.umubyeyi_wa_mwana}</div>
                                        </div>
                                      </div>
                                      <div className="mm-grid-card-tags">
                                        <span className={`mm-tag ${item.igitsina === "Gabo" ? "mm-tag-blue" : "mm-tag-rose"}`}>{item.igitsina}</span>
                                        {getNutritionTag(item.urwego_rw_imirire)}
                                        <span className={`mm-tag ${item.afashijwe === "Yego" ? "mm-tag-green" : "mm-tag-red"}`}>{item.afashijwe === "Yego" ? "Yafashijwe" : "Ntiyafashijwe"}</span>
                                      </div>
                                      <div className="mm-grid-card-row"><span className="mm-grid-card-lbl">Imyaka</span><span className="mm-grid-card-val">{item.imyaka}</span></div>
                                      <div className="mm-grid-card-row"><span className="mm-grid-card-lbl">Aderesi</span><span className="mm-grid-card-val">{item.aderesi || "—"}</span></div>
                                      <div className="mm-grid-card-actions">
                                        <button className="mm-act view" onClick={() => openDetail(item)}><i className="bi bi-eye"></i></button>
                                        <button className="mm-act edit" onClick={() => handleEdit(item)}><i className="bi bi-pencil"></i></button>
                                        <button className="mm-act del" onClick={() => confirmDelete(item)}><i className="bi bi-trash3"></i></button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* MOBILE */}
                          <div className="mm-mobile-cards">
                            {currentItems.map((item, idx) => {
                              const isUrgent = item.urwego_rw_imirire === "Bikabije" && item.afashijwe === "Oya";
                              const isExp = expandedCard === item.id;
                              return (
                                <div key={item.id} className={`mm-mcard ${isUrgent ? "urgent" : ""}`} style={{ animationDelay: `${idx * 0.035}s` }}
                                  onClick={() => setExpandedCard(isExp ? null : item.id)}>
                                  <div className="mm-mcard-head">
                                    <div className="mm-mcard-left">
                                      <div className="mm-mcard-avatar" style={{ background: getAvatarColor(item.amazina) }}>{getInitials(item.amazina)}</div>
                                      <div>
                                        <div className="mm-mcard-name">{item.amazina} {isUrgent && <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444", fontSize: "0.7rem" }}></i>}</div>
                                        <div className="mm-mcard-tags">
                                          {getNutritionTag(item.urwego_rw_imirire)}
                                          <span className={`mm-tag ${item.afashijwe === "Yego" ? "mm-tag-green" : "mm-tag-red"}`} style={{ fontSize: "0.6rem" }}>{item.afashijwe}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <span className={`mm-tag ${item.igitsina === "Gabo" ? "mm-tag-blue" : "mm-tag-rose"}`}>{item.igitsina}</span>
                                  </div>
                                  <div className="mm-mcard-row"><span className="mm-mcard-lbl">Imyaka</span><span className="mm-mcard-val">{item.imyaka}</span></div>
                                  <div className="mm-mcard-row"><span className="mm-mcard-lbl">Umubyeyi</span><span className="mm-mcard-val">{item.umubyeyi_wa_mwana}</span></div>
                                  {isExp && (
                                    <div style={{ animation: "mmUp 0.25s ease" }}>
                                      <div className="mm-mcard-row"><span className="mm-mcard-lbl">Aderesi</span><span className="mm-mcard-val">{item.aderesi || "—"}</span></div>
                                      <div className="mm-mcard-actions" onClick={e => e.stopPropagation()}>
                                        <button className="mm-act view" onClick={() => openDetail(item)}><i className="bi bi-eye"></i></button>
                                        <button className="mm-act edit" onClick={() => handleEdit(item)}><i className="bi bi-pencil"></i></button>
                                        <button className="mm-act del" onClick={() => confirmDelete(item)}><i className="bi bi-trash3"></i></button>
                                      </div>
                                    </div>
                                  )}
                                  <div className="mm-mcard-expand"><i className={`bi bi-chevron-${isExp ? "up" : "down"}`}></i></div>
                                </div>
                              );
                            })}
                          </div>

                          {renderPagination()}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ═══ STATISTICS TAB ═══ */}
              {activeTab === "statistics" && (
                <div className="mm-glass" style={{ animation: "mmScale 0.35s ease" }}>
                  <div className="mm-glass-body">
                    <h4 style={{ fontWeight: 800, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="bi bi-graph-up" style={{ color: "#f59e0b" }}></i> Imibare Yose
                    </h4>

                    <div className="mm-stats-detail">
                      {[
                        { label: "Abana Bose", value: stats.total, color: "#3b82f6" },
                        { label: "Gabo", value: stats.gabo, color: "#60a5fa" },
                        { label: "Gore", value: stats.gore, color: "#fb7185" },
                        { label: "Bikabije", value: stats.bikabije, color: "#ef4444" },
                        { label: "Bito", value: stats.bito, color: "#fbbf24" },
                        { label: "Bisanzwe", value: stats.bisanzwe, color: "#4ade80" },
                        { label: "Bafashijwe", value: stats.afashijwe, color: "#34d399" },
                        { label: "Ntibafashijwe", value: stats.ntibafashijwe, color: "#f87171" },
                        { label: "Imyaka y'Imikurire", value: stats.avgAge, color: "#fbbf24" },
                      ].map((s, i) => (
                        <div key={i} className="mm-stat blue" style={{ padding: "14px 10px" }}>
                          <div className="mm-stat-val" style={{ color: s.color, fontSize: "1.4rem" }}>{s.value}</div>
                          <div className="mm-stat-lbl">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    <h5 style={{ fontWeight: 700, marginBottom: 14, color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>
                      Urwego rw'Imirire
                    </h5>
                    <div className="mm-bar-chart">
                      {[
                        { label: "Bikabije", value: stats.bikabije, color: "#ef4444", max: stats.total },
                        { label: "Bito", value: stats.bito, color: "#fbbf24", max: stats.total },
                        { label: "Bisanzwe", value: stats.bisanzwe, color: "#4ade80", max: stats.total },
                      ].map((b, i) => (
                        <div key={i} className="mm-bar-row">
                          <div className="mm-bar-label">{b.label}</div>
                          <div className="mm-bar-track">
                            <div className="mm-bar-fill" style={{ width: `${b.max ? (b.value / b.max * 100) : 0}%`, background: `linear-gradient(90deg, ${b.color}, ${b.color}88)` }}></div>
                          </div>
                          <div className="mm-bar-val" style={{ color: b.color }}>{b.value}</div>
                        </div>
                      ))}
                    </div>

                    <h5 style={{ fontWeight: 700, marginBottom: 14, marginTop: 24, color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>
                      Ubufasha
                    </h5>
                    <div className="mm-bar-chart">
                      {[
                        { label: "Bafashijwe", value: stats.afashijwe, color: "#34d399", max: stats.total },
                        { label: "Ntibafashijwe", value: stats.ntibafashijwe, color: "#f87171", max: stats.total },
                      ].map((b, i) => (
                        <div key={i} className="mm-bar-row">
                          <div className="mm-bar-label">{b.label}</div>
                          <div className="mm-bar-track">
                            <div className="mm-bar-fill" style={{ width: `${b.max ? (b.value / b.max * 100) : 0}%`, background: `linear-gradient(90deg, ${b.color}, ${b.color}88)` }}></div>
                          </div>
                          <div className="mm-bar-val" style={{ color: b.color }}>{b.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ PRIORITY TAB ═══ */}
              {activeTab === "priority" && (
                <div className="mm-glass" style={{ animation: "mmScale 0.35s ease" }}>
                  <div className="mm-glass-body">
                    <h4 style={{ fontWeight: 800, marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="bi bi-exclamation-triangle-fill" style={{ color: "#ef4444" }}></i>
                      Abana Bakeneye Ubufasha Bwihutirwa
                    </h4>
                    <p style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.85rem", marginBottom: 20 }}>
                      Abana bafite imirire ikabije kandi ntibafashijwe
                    </p>

                    {urgentCases.length === 0 ? (
                      <div className="mm-empty">
                        <div className="mm-empty-icon" style={{ background: "rgba(16,185,129,0.05)", borderColor: "rgba(16,185,129,0.08)" }}>
                          <i className="bi bi-check-circle" style={{ color: "rgba(16,185,129,0.4)" }}></i>
                        </div>
                        <h5 style={{ color: "rgba(255,255,255,0.38)", fontWeight: 700, marginBottom: 4 }}>Nta bana bakeneye ubufasha bwihutirwa</h5>
                        <p style={{ color: "rgba(255,255,255,0.18)", fontSize: "0.82rem" }}>Byose bimeze neza!</p>
                      </div>
                    ) : (
                      urgentCases.map((item, idx) => (
                        <div key={item.id} className="mm-urgent-item" style={{ animation: `mmSlide 0.3s ease ${idx * 0.05}s both` }}
                          onClick={() => openDetail(item)}>
                          <div className="mm-urgent-pulse"></div>
                          <div className="mm-urgent-avatar" style={{ background: getAvatarColor(item.amazina) }}>{getInitials(item.amazina)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem", marginBottom: 2 }}>{item.amazina}</div>
                            <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)" }}>
                              {item.imyaka} imyaka · {item.umubyeyi_wa_mwana} · {item.aderesi}
                            </div>
                          </div>
                          {getNutritionTag("Bikabije")}
                          <button className="mm-act edit" style={{ marginLeft: 8 }} onClick={e => { e.stopPropagation(); handleEdit(item); }}>
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* ═══ ADD/EDIT MODAL ═══ */}
              {showModal && (
                <div className="mm-overlay" onClick={() => setShowModal(false)}>
                  <div className="mm-modal" onClick={e => e.stopPropagation()}>
                    <div className="mm-modal-head">
                      <div className="mm-modal-title">
                        <i className={`bi bi-${editId ? "pencil-square" : "person-plus-fill"}`} style={{ color: "#ef4444" }}></i>
                        {editId ? "Hindura Amakuru" : "Ongeraho Umwana"}
                      </div>
                      <button className="mm-modal-x" onClick={() => setShowModal(false)}>✕</button>
                    </div>
                    <div className="mm-modal-body">
                      <div className="mm-form-grid">
                        <div className="mm-field">
                          <label className="mm-label"><i className="bi bi-person" style={{ marginRight: 4, color: "#ef4444" }}></i>Amazina<span className="mm-req">*</span></label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-person mm-inp-icon"></i>
                            <input className={`mm-inp${errors.amazina ? " err" : ""}`} name="amazina" value={form.amazina} onChange={handleChange} placeholder="Amazina y'umwana" />
                          </div>
                          {errors.amazina && <div className="mm-err-msg">{errors.amazina}</div>}
                        </div>
                        <div className="mm-field">
                          <label className="mm-label"><i className="bi bi-calendar3" style={{ marginRight: 4, color: "#ef4444" }}></i>Imyaka<span className="mm-req">*</span></label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-calendar3 mm-inp-icon"></i>
                            <input className={`mm-inp${errors.imyaka ? " err" : ""}`} type="number" name="imyaka" value={form.imyaka} onChange={handleChange} placeholder="0-18" min="0" max="18" />
                          </div>
                          {errors.imyaka && <div className="mm-err-msg">{errors.imyaka}</div>}
                        </div>
                        <div className="mm-field">
                          <label className="mm-label"><i className="bi bi-gender-ambiguous" style={{ marginRight: 4, color: "#ef4444" }}></i>Igitsina</label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-gender-ambiguous mm-inp-icon"></i>
                            <select className="mm-sel" name="igitsina" value={form.igitsina} onChange={handleChange}>
                              <option value="Gabo">Gabo</option>
                              <option value="Gore">Gore</option>
                            </select>
                          </div>
                        </div>
                        <div className="mm-field">
                          <label className="mm-label"><i className="bi bi-heart-pulse" style={{ marginRight: 4, color: "#ef4444" }}></i>Urwego rw'Imirire</label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-heart-pulse mm-inp-icon"></i>
                            <select className="mm-sel" name="urwego_rw_imirire" value={form.urwego_rw_imirire} onChange={handleChange}>
                              <option value="Bisanzwe">Bisanzwe</option>
                              <option value="Bito">Bito</option>
                              <option value="Bikabije">Bikabije</option>
                            </select>
                          </div>
                        </div>
                        <div className="mm-field">
                          <label className="mm-label"><i className="bi bi-person-heart" style={{ marginRight: 4, color: "#ef4444" }}></i>Umubyeyi<span className="mm-req">*</span></label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-person-heart mm-inp-icon"></i>
                            <input className={`mm-inp${errors.umubyeyi_wa_mwana ? " err" : ""}`} name="umubyeyi_wa_mwana" value={form.umubyeyi_wa_mwana} onChange={handleChange} placeholder="Amazina y'umubyeyi" />
                          </div>
                          {errors.umubyeyi_wa_mwana && <div className="mm-err-msg">{errors.umubyeyi_wa_mwana}</div>}
                        </div>
                        <div className="mm-field">
                          <label className="mm-label"><i className="bi bi-hand-thumbs-up" style={{ marginRight: 4, color: "#ef4444" }}></i>Afashijwe?</label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-hand-thumbs-up mm-inp-icon"></i>
                            <select className="mm-sel" name="afashijwe" value={form.afashijwe} onChange={handleChange}>
                              <option value="Oya">Oya</option>
                              <option value="Yego">Yego</option>
                            </select>
                          </div>
                        </div>
                        <div className="mm-field mm-form-full">
                          <label className="mm-label"><i className="bi bi-geo-alt" style={{ marginRight: 4, color: "#ef4444" }}></i>Aderesi<span className="mm-req">*</span></label>
                          <div className="mm-inp-wrap">
                            <i className="bi bi-geo-alt mm-inp-icon"></i>
                            <input className={`mm-inp${errors.aderesi ? " err" : ""}`} name="aderesi" value={form.aderesi} onChange={handleChange} placeholder="Aho batuye" />
                          </div>
                          {errors.aderesi && <div className="mm-err-msg">{errors.aderesi}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="mm-modal-foot">
                      <button className="mm-btn mm-btn-ghost" onClick={() => setShowModal(false)} disabled={saving}><i className="bi bi-x-lg"></i> Funga</button>
                      <button className="mm-btn mm-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="bi bi-arrow-clockwise mm-spin"></i> Tegereza...</> : <><i className={`bi bi-${editId ? "check-lg" : "plus-lg"}`}></i> {editId ? "Hindura" : "Bika"}</>}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ DETAIL MODAL ═══ */}
              {showDetailModal && detailItem && (
                <div className="mm-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="mm-modal" onClick={e => e.stopPropagation()}>
                    <div className="mm-modal-head">
                      <div className="mm-modal-title"><i className="bi bi-person-badge" style={{ color: "#22d3ee" }}></i> Amakuru Arambuye</div>
                      <button className="mm-modal-x" onClick={() => setShowDetailModal(false)}>✕</button>
                    </div>
                    <div className="mm-modal-body">
                      <div style={{ textAlign: "center", marginBottom: 22 }}>
                        <div className="mm-detail-avatar" style={{ background: getAvatarColor(detailItem.amazina) }}>{getInitials(detailItem.amazina)}</div>
                        <h3 style={{ fontWeight: 800, color: "#fff", fontSize: "1.2rem", marginBottom: 6 }}>{detailItem.amazina}</h3>
                        <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                          <span className={`mm-tag ${detailItem.igitsina === "Gabo" ? "mm-tag-blue" : "mm-tag-rose"}`}>{detailItem.igitsina}</span>
                          {getNutritionTag(detailItem.urwego_rw_imirire)}
                          <span className={`mm-tag ${detailItem.afashijwe === "Yego" ? "mm-tag-green" : "mm-tag-red"}`}>{detailItem.afashijwe === "Yego" ? "Yafashijwe" : "Ntiyafashijwe"}</span>
                        </div>
                      </div>
                      <div className="mm-detail-grid">
                        {[
                          { l: "Amazina", v: detailItem.amazina, i: "person" },
                          { l: "Imyaka", v: detailItem.imyaka, i: "calendar3" },
                          { l: "Igitsina", v: detailItem.igitsina, i: "gender-ambiguous" },
                          { l: "Umubyeyi", v: detailItem.umubyeyi_wa_mwana || "—", i: "person-heart" },
                          { l: "Aderesi", v: detailItem.aderesi || "—", i: "geo-alt" },
                          { l: "Imirire", v: detailItem.urwego_rw_imirire, i: "heart-pulse" },
                          { l: "Afashijwe", v: detailItem.afashijwe === "Yego" ? "Yego — Yafashijwe" : "Oya — Ntiyafashijwe", i: "hand-thumbs-up" },
                        ].map((d, i) => (
                          <div key={i} className={`mm-detail-item ${i === 6 ? "mm-form-full" : ""}`}>
                            <div className="mm-detail-lbl"><i className={`bi bi-${d.i}`}></i> {d.l}</div>
                            <div className="mm-detail-val">{d.v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mm-modal-foot">
                      <button className="mm-btn mm-btn-ghost" onClick={() => setShowDetailModal(false)}><i className="bi bi-x-lg"></i> Funga</button>
                      <button className="mm-btn mm-btn-primary" onClick={() => { setShowDetailModal(false); handleEdit(detailItem); }}><i className="bi bi-pencil"></i> Hindura</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ DELETE MODAL ═══ */}
              {showDeleteModal && deleteTarget && (
                <div className="mm-overlay" onClick={() => { if (!deleting) setShowDeleteModal(false); }}>
                  <div className="mm-modal mm-modal-sm" onClick={e => e.stopPropagation()}>
                    <div className="mm-modal-body" style={{ textAlign: "center", padding: "32px 24px" }}>
                      <div className="mm-delete-icon"><i className="bi bi-trash3"></i></div>
                      <h4 style={{ fontWeight: 800, color: "#fff", fontSize: "1.15rem", marginBottom: 6 }}>Siba Amakuru?</h4>
                      <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.88rem", marginBottom: 4 }}>Uremeza ko ushaka gusiba:</p>
                      <p style={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem", marginBottom: 4 }}>{deleteTarget.amazina}</p>
                      <p style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.75rem", marginBottom: 24 }}>
                        <i className="bi bi-exclamation-triangle" style={{ marginRight: 4 }}></i>Iki gikorwa ntikigarurwa.
                      </p>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <button className="mm-btn mm-btn-ghost" style={{ flex: 1 }} onClick={() => setShowDeleteModal(false)} disabled={deleting}>Oya, Bireke</button>
                        <button className="mm-btn mm-btn-danger" style={{ flex: 1 }} onClick={handleDelete} disabled={deleting}>
                          {deleting ? <><i className="bi bi-arrow-clockwise mm-spin"></i> Tegereza...</> : <><i className="bi bi-trash3"></i> Yego, Siba</>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* FAB */}
              <button className="mm-fab" onClick={() => { resetForm(); setShowModal(true); }} aria-label="Ongeraho">
                <i className="bi bi-plus-lg"></i>
              </button>

              <div className="mm-footer">
                <i className="bi bi-shield-lock-fill" style={{ marginRight: 5 }}></i>
                Amakuru yose arindwa — akoreshwa ku buryo bw'amategeko
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AbanaMirireMibi;